import { useState, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { customInstance, useGetProjects } from '@edison/api-client-react';
import {
  AlertCircle, Plus, TriangleAlert, RotateCcw, ChevronDown, ChevronRight, CheckCircle2, Circle,
} from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { useLocation } from 'wouter';
import { formatFy } from '@/lib/utils.js';
import { MethodologyCard, type ApportionmentView } from './methodology-card.js';
import { CandidateQueue, COST_CATEGORY_OPTIONS } from './candidate-queue.js';
import { EligibilityChip, type EligibilityStatus } from './eligibility-chip.js';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ProviderSummary { totalAud: number; lastSyncedAt: string | null }

interface ExpenditureSummary {
  totalAud: number;
  totalClaimable: number;
  unattributedCount: number;
  configuredProviders: string[];
  fx?: { usdAudRate: number | null; pendingConversionCount: number };
  byProvider: Record<string, ProviderSummary>;
  byCategory: Record<string, number>;
  byManualCategory?: Record<string, number>;
  monthlyByProvider: Array<Record<string, number | string>>;
  monthlyByCategory: Array<Record<string, number | string>>;
  byProject: Array<{
    projectId: string | null;
    projectName: string;
    totalAud: number;
    cloud: number;
    llm_tokens: number;
    software_license: number;
    hardware: number;
    other: number;
  }>;
}

// Banner to view/set the admin USD→AUD conversion rate. Shows when foreign-currency cost rows
// have no rate applied ("conversion pending"), or once a rate is set (so it can be adjusted).
function FxRateBanner({ fx, onSaved }: { fx?: { usdAudRate: number | null; pendingConversionCount: number }; onSaved: () => void }) {
  const pending = fx?.pendingConversionCount ?? 0;
  const current = fx?.usdAudRate ?? null;
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Nothing to show: no pending conversions and no rate configured.
  if (pending === 0 && current == null) return null;

  const save = async () => {
    const rate = parseFloat(value);
    if (!Number.isFinite(rate) || rate <= 0) { setError('Enter a positive number (e.g. 1.52)'); return; }
    setSaving(true); setError('');
    try {
      await customInstance({ url: '/api/integrations', method: 'PUT', data: { provider: 'fx', configKey: 'usd_aud', configValue: String(rate) } });
      setValue('');
      onSaved();
    } catch { setError('Failed to save rate'); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 p-4">
      <div className="flex items-start gap-3">
        <TriangleAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
            {pending > 0
              ? `${pending} cost ${pending === 1 ? 'row is' : 'rows are'} in a foreign currency with no conversion rate applied.`
              : 'USD→AUD conversion rate'}
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
            {current != null
              ? `Current rate: 1 USD = ${current} AUD. Re-sync a provider to apply it to any pending rows.`
              : 'Set the USD→AUD rate (e.g. the ATO/RBA figure). Foreign-currency amounts stay unconverted until it is set — never guessed.'}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <input
              type="number" step="0.0001" min="0" inputMode="decimal"
              value={value} onChange={(e) => setValue(e.target.value)}
              placeholder={current != null ? String(current) : '1.52'}
              className="h-8 w-28 rounded-md border border-amber-300 bg-background px-2 text-sm"
              aria-label="USD to AUD rate"
            />
            <Button variant="primary" size="sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : current != null ? 'Update rate' : 'Set rate'}
            </Button>
            {error && <span className="text-xs text-destructive">{error}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

interface GlSetupStatus {
  hasUntaggedManualRows: boolean;
  hasAccountMap: boolean;
  hasSnapshots: boolean;
}

// Admin-facing GL reconciliation setup checklist — the internal, actionable counterpart to the
// audit portal's read-only empty state. Shows while any of the three steps is incomplete;
// dismissible (session-local — reappears on next visit if still incomplete, since nothing was
// actually resolved).
function GlSetupChecklist({ status }: { status: GlSetupStatus | undefined }) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || !status) return null;

  const allDone = !status.hasUntaggedManualRows && status.hasAccountMap && status.hasSnapshots;
  if (allDone) return null;

  const steps: Array<{ label: string; done: boolean; hint: string }> = [
    {
      label: 'Tag manual cost rows with a vendor',
      done: !status.hasUntaggedManualRows,
      hint: 'Set a vendor on manual rows in All Line Items below so they tie to a Xero account.',
    },
    {
      label: 'Map vendors to Xero GL accounts',
      done: status.hasAccountMap,
      hint: 'Add vendor → account terms under Integrations (Xero).',
    },
    {
      label: 'Sync the Xero ledger',
      done: status.hasSnapshots,
      hint: 'Run a Xero sync so ledger account snapshots are available for reconciliation.',
    },
  ];

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 p-4">
      <div className="flex items-start gap-3">
        <TriangleAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              Finish GL reconciliation setup
            </p>
            <button
              onClick={() => setDismissed(true)}
              aria-label="Dismiss"
              className="text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200"
            >
              ✕
            </button>
          </div>
          <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
            Complete these steps so non-labour costs can be reconciled against the Xero general ledger.
          </p>
          <ul className="mt-2.5 space-y-1.5">
            {steps.map((step) => (
              <li key={step.label} className="flex items-start gap-2">
                {step.done ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" aria-hidden="true" />
                ) : (
                  <Circle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
                )}
                <div className="min-w-0">
                  <span className={`text-xs font-medium ${step.done ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-800 dark:text-amber-300'}`}>
                    {step.done ? `Done — ${step.label}` : step.label}
                  </span>
                  {!step.done && (
                    <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80">{step.hint}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

interface CostAllocation {
  id: string;
  tenantId: string;
  claimId: string | null;
  projectId: string | null;
  category: string;
  amount: string;
  periodStart: string;
  periodEnd: string;
  source: string;
  sourceRef: string | null;
  matchConfidence: string | null;
  description: string | null;
  notes: string | null;
  vendor: string | null;
  eligibilityStatus: EligibilityStatus;
  claimablePercent: string;
  eligibilityReason: string | null;
  apportionmentBasis: string | null;
  createdBy: string;
  createdAt: string;
}

interface LineItemsData {
  items: CostAllocation[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

interface ExpenditureAccount {
  provider: string;
  externalId: string;
  label: string;
  totalAud: number;
  services: Array<{ name: string; totalAud: number }>;
  overrideId: string | null;
  mappedProjectId: string | null;
  splits?: Array<{ id: string; projectId: string; splitPercentage: number | null }>;
}

interface ProjectCostOverride {
  id: string;
  tenantId: string;
  projectId: string;
  provider: string;
  externalId: string;
  label: string | null;
  splitPercentage: string | null;
  createdBy: string;
  createdAt: string;
  project?: { id: string; name: string } | null;
}

// ─── Income year helpers ─────────────────────────────────────────────────────

function currentIncomeYear(): string {
  const now = new Date();
  const year = now.getFullYear();
  // Default to the most recently COMPLETED Australian FY (ended last June 30)
  return now.getMonth() >= 6 ? `FY${year}` : `FY${year - 1}`;
}

function parseIncomeYear(fy: string): { start: string; end: string } | null {
  const match = /^FY(\d{4})$/.exec(fy);
  if (!match) return null;
  const endYear = parseInt(match[1]!, 10);
  return { start: `${endYear - 1}-07-01`, end: `${endYear}-06-30` };
}


function fyOptions(): string[] {
  const current = currentIncomeYear();
  const endYear = parseInt(current.replace('FY', ''));
  return [current, `FY${endYear - 1}`, `FY${endYear - 2}`];
}

// ─── Format helpers ───────────────────────────────────────────────────────────

function formatAUD(amount: number): string {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}k`;
  return `$${amount.toFixed(2)}`;
}

function formatMonthLabel(yyyyMm: string): string {
  const [year, month] = yyyyMm.split('-');
  return new Date(`${year}-${month}-01`).toLocaleDateString('en-AU', { month: 'short' }).toUpperCase();
}

function formatMonthFull(yyyyMm: string): string {
  const [year, month] = yyyyMm.split('-');
  return new Date(`${year}-${month}-01`).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
}

function timeAgo(isoStr: string): string {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── Provider metadata ───────────────────────────────────────────────────────

const PROVIDER_META: Array<{
  source: string;
  label: string;
  color: string;
  integrationKey: string;
  pillBg: string;
  pillBorder: string;
  dotColor: string;
  textColor: string;
  subTextColor: string;
}> = [
  {
    source: 'api_aws', label: 'AWS', color: '#f59e0b', integrationKey: 'aws',
    pillBg: '#fefce8', pillBorder: '#fef08a', dotColor: '#d97706',
    textColor: '#92400e', subTextColor: '#a16207',
  },
  {
    source: 'api_gcp', label: 'GCP', color: '#3b82f6', integrationKey: 'gcp',
    pillBg: '#eff6ff', pillBorder: '#bfdbfe', dotColor: '#3b82f6',
    textColor: '#1e3a5f', subTextColor: '#1d4ed8',
  },
  {
    source: 'api_openai', label: 'OpenAI', color: '#10b981', integrationKey: 'openai',
    pillBg: '#f0fdf4', pillBorder: '#bbf7d0', dotColor: '#059669',
    textColor: '#064e3b', subTextColor: '#047857',
  },
  {
    source: 'api_anthropic', label: 'Anthropic', color: '#8b5cf6', integrationKey: 'anthropic',
    pillBg: '#faf5ff', pillBorder: '#e9d5ff', dotColor: '#7c3aed',
    textColor: '#4c1d95', subTextColor: '#6d28d9',
  },
  {
    source: 'api_replit', label: 'Replit', color: '#f97316', integrationKey: 'replit',
    pillBg: '#fff7ed', pillBorder: '#fed7aa', dotColor: '#ea580c',
    textColor: '#7c2d12', subTextColor: '#c2410c',
  },
  {
    // Xero GL costs enter via the review queue (approved candidates → source 'api_xero'), not a
    // sync button — integrationKey is '' so it never appears as a "Connect / Sync" provider pill,
    // but it IS a chart segment / legend entry / line-item filter and resolves via sourceLabel.
    source: 'api_xero', label: 'Xero GL', color: '#0ea5e9', integrationKey: '',
    pillBg: '#f0f9ff', pillBorder: '#bae6fd', dotColor: '#0284c7',
    textColor: '#0c4a6e', subTextColor: '#0369a1',
  },
  {
    source: 'manual', label: 'Manual', color: '#6b7280', integrationKey: '',
    pillBg: '#f8fafc', pillBorder: '#e2e8f0', dotColor: '#94a3b8',
    textColor: '#374151', subTextColor: '#9ca3af',
  },
];

const CATEGORY_META: Array<{ key: string; label: string; color: string }> = [
  { key: 'cloud', label: 'Cloud Hosting', color: '#3b82f6' },
  { key: 'llm_tokens', label: 'AI / LLM Services', color: '#8b5cf6' },
  { key: 'software_license', label: 'Software Licences', color: '#10b981' },
  { key: 'hardware', label: 'Hardware & Equipment', color: '#64748b' },
  { key: 'other', label: 'Other', color: '#6b7280' },
  { key: 'admin_overhead', label: 'Admin Overhead', color: '#f59e0b' },
  { key: 'software_subscription', label: 'Software Subscription', color: '#06b6d4' },
  { key: 'insurance', label: 'Insurance', color: '#ec4899' },
  { key: 'consultant_fees', label: 'Consultant Fees', color: '#14b8a6' },
  { key: 'company_setup', label: 'Company Setup', color: '#a855f7' },
  { key: 'rdti_claim_prep', label: 'R&DTI Claim Prep', color: '#ef4444' },
];

// ─── SVG Stacked Bar Chart ───────────────────────────────────────────────────

function TrendChart({
  data,
  mode,
}: {
  data: Array<Record<string, number | string>>;
  mode: 'provider' | 'category';
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const W = 600;
  const H = 240;
  const PAD_LEFT = 52;
  const PAD_RIGHT = 8;
  const PAD_TOP = 16;
  const PAD_BOTTOM = 36;
  const chartW = W - PAD_LEFT - PAD_RIGHT;
  const chartH = H - PAD_TOP - PAD_BOTTOM;

  const providerSegments = PROVIDER_META;
  const categorySegments = CATEGORY_META;
  const segments = mode === 'provider' ? providerSegments : categorySegments;
  const segmentKeys = mode === 'provider'
    ? providerSegments.map((s) => s.source)
    : categorySegments.map((s) => s.key);

  const maxVal = Math.max(
    ...data.map((m) => segmentKeys.reduce((s, k) => s + (Number(m[k]) || 0), 0)),
    1,
  );

  const barGap = 3;
  const barW = Math.max(4, chartW / data.length - barGap);

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
    pct: t,
    value: maxVal * t,
    y: PAD_TOP + chartH * (1 - t),
  }));

  // Build tooltip for hovered bar
  const tooltipData = hoveredIndex !== null ? (() => {
    const m = data[hoveredIndex]!;
    const total = segmentKeys.reduce((s, k) => s + (Number(m[k]) || 0), 0);
    const rows = segments
      .map((seg, si) => ({ seg, val: Number(m[segmentKeys[si]!]) || 0 }))
      .filter(({ val }) => val > 0);
    return { month: String(m['month']), total, rows };
  })() : null;

  // Convert SVG x-coordinate to container percentage for HTML tooltip positioning
  const getBarCenterPct = (i: number) => {
    const x = PAD_LEFT + i * (barW + barGap) + barW / 2;
    return x / W;
  };

  return (
    <div ref={containerRef} className="relative w-full" style={{ height: H }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Monthly non-labour expenditure by ${mode}`}
        className="w-full h-full overflow-visible"
        preserveAspectRatio="none"
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        }}
        onMouseLeave={() => { setHoveredIndex(null); setMousePos(null); }}
      >
        {yTicks.map((tick) => (
          <g key={tick.pct}>
            <line x1={PAD_LEFT} y1={tick.y} x2={W - PAD_RIGHT} y2={tick.y}
              stroke="currentColor" strokeOpacity={tick.pct === 0 ? 0.2 : 0.07}
              strokeWidth={tick.pct === 0 ? 1.5 : 1} />
            <text x={PAD_LEFT - 6} y={tick.y + 4} textAnchor="end" fontSize={10}
              fill="currentColor" fillOpacity={0.5}>
              {tick.value > 0 ? formatAUD(tick.value) : ''}
            </text>
          </g>
        ))}
        {data.map((m, i) => {
          const x = PAD_LEFT + i * (barW + barGap);
          let stackY = PAD_TOP + chartH;
          const isHovered = hoveredIndex === i;
          return (
            <g key={String(m['month'])}>
              {segments.map((seg, si) => {
                const key = segmentKeys[si]!;
                const val = Number(m[key]) || 0;
                const barH = (val / maxVal) * chartH;
                if (barH < 0.5) return null;
                stackY -= barH;
                return (
                  <rect key={si} x={x} y={stackY} width={barW} height={barH}
                    fill={seg.color} fillOpacity={isHovered ? 1 : 0.8} rx={si === 0 ? 2 : 0} />
                );
              })}
              <text x={x + barW / 2} y={H - 8} textAnchor="middle" fontSize={9}
                fill="currentColor" fillOpacity={isHovered ? 0.9 : 0.55}>
                {formatMonthLabel(String(m['month']))}
              </text>
              {/* Transparent hover zone covers full column */}
              <rect
                x={x - barGap / 2} y={PAD_TOP}
                width={barW + barGap} height={chartH + PAD_BOTTOM / 2}
                fill="transparent"
                style={{ cursor: 'default' }}
                onMouseEnter={() => setHoveredIndex(i)}
              />
            </g>
          );
        })}
      </svg>

      {/* HTML tooltip overlay — positioned relative to mouse */}
      {tooltipData && mousePos && hoveredIndex !== null && (() => {
        const containerWidth = containerRef.current?.getBoundingClientRect().width ?? 600;
        const barCenterPct = getBarCenterPct(hoveredIndex);
        const toRight = barCenterPct < 0.55;
        const offsetX = toRight ? mousePos.x + 14 : mousePos.x - 14;
        const clampedX = toRight
          ? Math.min(offsetX, containerWidth - 180)
          : Math.max(offsetX - 168, 0);
        const clampedY = Math.max(0, Math.min(mousePos.y - 20, H - 140));

        return (
          <div
            className="absolute z-20 pointer-events-none"
            style={{ left: clampedX, top: clampedY }}
          >
            <div className="bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden min-w-[168px]"
              style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06)' }}>
              {/* Header */}
              <div className="px-3.5 pt-3 pb-2 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-900 tracking-wide">
                  {formatMonthFull(tooltipData.month)}
                </p>
              </div>
              {/* Rows */}
              <div className="px-3.5 py-2 space-y-1.5">
                {tooltipData.rows.map(({ seg, val }, ri) => (
                  <div key={ri} className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span
                        className="shrink-0 h-2 w-2 rounded-sm"
                        style={{ backgroundColor: seg.color }}
                      />
                      <span className="text-xs text-gray-500 truncate">
                        {mode === 'provider'
                          ? (seg as typeof PROVIDER_META[0]).label
                          : (seg as typeof CATEGORY_META[0]).label}
                      </span>
                    </div>
                    <span className="text-xs font-medium text-gray-900 tabular-nums shrink-0">
                      {formatAUD(val)}
                    </span>
                  </div>
                ))}
              </div>
              {/* Total */}
              <div className="px-3.5 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-600">Total</span>
                <span className="text-xs font-bold text-gray-900 tabular-nums">
                  {formatAUD(tooltipData.total)}
                </span>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Add Cost Slide Panel ─────────────────────────────────────────────────────

function AddCostPanel({
  open,
  onClose,
  projects,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  projects: Array<{ id: string; name: string }>;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState({
    category: 'cloud',
    vendor: '',
    amount: '',
    periodStart: '',
    periodEnd: '',
    projectId: '',
    description: '',
    notes: '',
    eligibilityStatus: 'claimable' as EligibilityStatus,
    claimablePercent: '',
    eligibilityReason: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => setForm({
    category: 'cloud', vendor: '', amount: '', periodStart: '', periodEnd: '', projectId: '',
    description: '', notes: '', eligibilityStatus: 'claimable', claimablePercent: '', eligibilityReason: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await customInstance({
        url: '/api/cost-allocations',
        method: 'POST',
        data: {
          category: form.category,
          amount: parseFloat(form.amount),
          periodStart: form.periodStart,
          periodEnd: form.periodEnd,
          projectId: form.projectId || undefined,
          description: form.description || undefined,
          notes: form.notes || undefined,
          vendor: form.vendor.trim().toLowerCase() || undefined,
          eligibilityStatus: form.eligibilityStatus,
          claimablePercent: form.claimablePercent.trim() !== '' ? parseFloat(form.claimablePercent) : undefined,
          eligibilityReason: form.eligibilityReason.trim() || undefined,
        },
      });
      resetForm();
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(msg ?? 'Failed to save cost. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const fieldCls = 'w-full rounded border bg-background px-3 py-2 text-sm';
  const labelCls = 'text-xs font-medium block mb-1';

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <aside className="w-full max-w-md bg-card border-l shadow-xl flex flex-col overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="font-semibold text-sm">Add Cost</h2>
          <button onClick={onClose} aria-label="Close panel" className="text-muted-foreground hover:text-foreground">
            ✕
          </button>
        </div>
        <form onSubmit={(e) => void handleSubmit(e)} className="flex-1 p-5 space-y-4">
          <div>
            <label className={labelCls} htmlFor="ac-category">Category *</label>
            <select id="ac-category" className={fieldCls} value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
              {COST_CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="ac-vendor">Vendor</label>
            <input id="ac-vendor" type="text" className={fieldCls} list="vendor-options" maxLength={50}
              value={form.vendor} onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))}
              placeholder="e.g. aws, replit, claude_teams" />
            <datalist id="vendor-options">
              <option value="aws" /><option value="gcp" /><option value="anthropic" />
              <option value="openai" /><option value="replit" /><option value="claude_teams" />
              <option value="github" /><option value="jetbrains" />
            </datalist>
            <p className="text-[11px] text-muted-foreground mt-1">Which provider this cost is from — ties it to a Xero GL account. Free text allowed.</p>
          </div>
          <div>
            <label className={labelCls} htmlFor="ac-amount">Amount (AUD) *</label>
            <input id="ac-amount" type="number" step="0.01" min="0.01" className={fieldCls}
              value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              placeholder="0.00" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls} htmlFor="ac-eligibility">Eligibility</label>
              <select id="ac-eligibility" className={fieldCls} value={form.eligibilityStatus}
                onChange={(e) => setForm((f) => ({ ...f, eligibilityStatus: e.target.value as EligibilityStatus }))}>
                <option value="claimable">Claimable</option>
                <option value="needs_review">Needs review</option>
                <option value="excluded">Excluded</option>
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="ac-percent">Claimable %</label>
              <input id="ac-percent" type="number" step="0.1" min="0" max="100" className={fieldCls}
                value={form.claimablePercent} onChange={(e) => setForm((f) => ({ ...f, claimablePercent: e.target.value }))}
                placeholder="100" />
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="ac-elig-reason">
              Eligibility reason{form.eligibilityStatus === 'excluded' && <span className="text-destructive"> *</span>}
            </label>
            <input id="ac-elig-reason" type="text" className={fieldCls} maxLength={2000}
              value={form.eligibilityReason} onChange={(e) => setForm((f) => ({ ...f, eligibilityReason: e.target.value }))}
              placeholder="Required when excluding — e.g. business establishment cost, not R&D" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls} htmlFor="ac-start">Period start *</label>
              <input id="ac-start" type="date" className={fieldCls} value={form.periodStart}
                onChange={(e) => setForm((f) => ({ ...f, periodStart: e.target.value }))} required />
            </div>
            <div>
              <label className={labelCls} htmlFor="ac-end">Period end *</label>
              <input id="ac-end" type="date" className={fieldCls} value={form.periodEnd}
                onChange={(e) => setForm((f) => ({ ...f, periodEnd: e.target.value }))} required />
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="ac-project">Project</label>
            <select id="ac-project" className={fieldCls} value={form.projectId}
              onChange={(e) => setForm((f) => ({ ...f, projectId: e.target.value }))}>
              <option value="">— Unattributed —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="ac-desc">Description</label>
            <input id="ac-desc" type="text" className={fieldCls} maxLength={500}
              value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="e.g. AWS hosting for Talaria API — June 2026" />
          </div>
          <div>
            <label className={labelCls} htmlFor="ac-notes">Attribution notes</label>
            <textarea id="ac-notes" className={`${fieldCls} resize-none`} rows={3} maxLength={2000}
              value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. 60% of $200 team spend — 3 of 5 Repls were R&D-tagged projects" />
            <p className="text-xs text-muted-foreground mt-1">Document your apportionment basis here for audit purposes.</p>
          </div>
          {error && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" aria-hidden="true" /> {error}
            </p>
          )}
          <div className="flex gap-2 pt-2">
            <Button type="submit" variant="primary" className="flex-1" disabled={saving}>
              {saving ? 'Saving…' : 'Add Cost'}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </aside>
    </div>
  );
}

// ─── Sync status entry ───────────────────────────────────────────────────────

function SyncStatusEntry({
  label,
  status,
  isSyncing,
}: {
  label: string;
  status: { created: number; updated: number; unchanged: number; rows?: number; at: string; error?: string; warnings?: string[] } | undefined;
  isSyncing: boolean;
}) {
  if (isSyncing) {
    return (
      <span className="inline-flex items-center gap-1 text-xs whitespace-nowrap">
        <svg className="animate-spin h-2.5 w-2.5 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
        <span className="font-semibold text-foreground">{label}</span>
        <span className="text-amber-600">syncing…</span>
      </span>
    );
  }
  if (!status) return null;
  if (status.error) {
    return (
      <span className="inline-flex items-center gap-1 text-xs whitespace-nowrap">
        <span className="text-destructive">✗</span>
        <span className="font-semibold text-foreground">{label}</span>
        <span className="text-destructive">{status.error.slice(0, 60)}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs whitespace-nowrap">
      <svg className="h-2.5 w-2.5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12" />
      </svg>
      <span className="font-semibold text-foreground">{label}</span>
      {status.rows === 0 && <span className="text-amber-600 font-medium">0 rows fetched</span>}
      {(status.rows ?? 1) > 0 && status.created > 0 && <span className="text-emerald-600 font-medium">{status.created} added</span>}
      {(status.rows ?? 1) > 0 && status.updated > 0 && <span className="text-blue-600 font-medium"> · {status.updated} updated</span>}
      {/* Partial-sync warnings (e.g. AWS declined out-of-retention months): the sync SUCCEEDED —
          render as a warning on the success entry, never as a failure. */}
      {status.warnings && status.warnings.length > 0 && (
        <span className="text-amber-600" title={status.warnings.join('\n')}> · {status.warnings[0]!.slice(0, 90)}</span>
      )}
      <span className="text-muted-foreground"> · {timeAgo(status.at)}</span>
    </span>
  );
}

// ─── Expanded project line items ─────────────────────────────────────────────

function ProjectItemsExpanded({
  projectId,
  incomeYear,
  isUnattributed,
}: {
  projectId: string | null;
  incomeYear: string;
  isUnattributed: boolean;
}) {
  const query = useQuery({
    queryKey: ['expenditure-project-items', incomeYear, isUnattributed ? 'unattributed' : projectId],
    queryFn: () =>
      customInstance<{ data: LineItemsData }>({
        url: '/api/expenditure/line-items',
        method: 'GET',
        params: {
          incomeYear,
          ...(isUnattributed ? { unattributed: 'true' } : { projectId }),
          limit: 100,
        },
      }).then((r) => r.data),
  });

  if (query.isLoading) {
    return (
      <tr className="bg-muted/20 border-b">
        <td colSpan={8} className="px-10 py-2 text-xs text-muted-foreground italic">Loading…</td>
      </tr>
    );
  }

  const items = query.data?.items ?? [];
  if (items.length === 0) {
    return (
      <tr className="bg-muted/20 border-b">
        <td colSpan={8} className="px-10 py-2 text-xs text-muted-foreground italic">No line items found.</td>
      </tr>
    );
  }

  return (
    <>
      {items.map((item) => (
        <tr key={item.id} className="bg-muted/20 border-b text-xs">
          <td className="px-10 py-2 text-muted-foreground">{item.description ?? '—'}</td>
          <td className="text-right px-3 py-2 text-muted-foreground font-mono">{item.category === 'cloud' ? formatAUD(parseFloat(item.amount)) : '—'}</td>
          <td className="text-right px-3 py-2 text-muted-foreground font-mono">{item.category === 'llm_tokens' ? formatAUD(parseFloat(item.amount)) : '—'}</td>
          <td className="text-right px-3 py-2 text-muted-foreground font-mono">{item.category === 'software_license' ? formatAUD(parseFloat(item.amount)) : '—'}</td>
          <td className="text-right px-3 py-2 text-muted-foreground font-mono">{item.category === 'hardware' ? formatAUD(parseFloat(item.amount)) : '—'}</td>
          <td className="text-right px-3 py-2 text-muted-foreground font-mono">{item.category === 'other' ? formatAUD(parseFloat(item.amount)) : '—'}</td>
          <td className="text-right px-4 py-2 font-mono">{formatAUD(parseFloat(item.amount))}</td>
          <td className="text-right px-4 py-2 text-muted-foreground">—</td>
        </tr>
      ))}
    </>
  );
}

// ─── Main page component ─────────────────────────────────────────────────────

export function ExpenditurePage() {
  const [incomeYear, setIncomeYear] = useState(currentIncomeYear);
  const [chartMode, setChartMode] = useState<'provider' | 'category'>('provider');
  const [expandedProjects, setExpandedProjects] = useState<Set<string | null>>(new Set());
  const [providerFilter, setProviderFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  const [syncingProviders, setSyncingProviders] = useState<Set<string>>(new Set());
  const [syncStatus, setSyncStatus] = useState<Record<string, { created: number; updated: number; unchanged: number; rows?: number; at: string; error?: string; warnings?: string[] }>>({});
  const [assigningItems, setAssigningItems] = useState<Set<string>>(new Set());
  const [mappingOpen, setMappingOpen] = useState(false);
  const [excludedOpen, setExcludedOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // pending new split rows: key = "provider:externalId"
  const [pendingSplits, setPendingSplits] = useState<Record<string, { projectId: string; pct: string }>>({});
  const unattributedRef = useRef<HTMLTableRowElement | null>(null);
  const [, navigate] = useLocation();
  const qc = useQueryClient();

  const summaryQuery = useQuery({
    queryKey: ['expenditure-summary', incomeYear],
    queryFn: () =>
      customInstance<{ data: ExpenditureSummary }>({
        url: '/api/expenditure/summary',
        method: 'GET',
        params: { incomeYear },
      }).then((r) => r.data),
  });

  const lineItemsQuery = useQuery({
    queryKey: ['expenditure-line-items', incomeYear, providerFilter, categoryFilter, search],
    queryFn: () =>
      customInstance<{ data: LineItemsData }>({
        url: '/api/expenditure/line-items',
        method: 'GET',
        params: {
          incomeYear,
          ...(providerFilter ? { provider: providerFilter } : {}),
          ...(categoryFilter ? { category: categoryFilter } : {}),
          ...(search ? { search } : {}),
          limit: 300,
        },
      }).then((r) => r.data),
  });

  const accountsQuery = useQuery({
    queryKey: ['expenditure-accounts'],
    queryFn: () =>
      customInstance<{ data: ExpenditureAccount[] }>({
        url: '/api/expenditure/accounts',
        method: 'GET',
      }).then((r) => r.data),
  });

  const overridesQuery = useQuery({
    queryKey: ['expenditure-overrides'],
    queryFn: () =>
      customInstance<{ data: ProjectCostOverride[] }>({
        url: '/api/expenditure/overrides',
        method: 'GET',
      }).then((r) => r.data),
  });

  const glSetupStatusQuery = useQuery({
    queryKey: ['expenditure-gl-setup-status'],
    queryFn: () =>
      customInstance<{ data: GlSetupStatus }>({
        url: '/api/expenditure/gl-setup-status',
        method: 'GET',
      }).then((r) => r.data),
  });

  const apportionmentQuery = useQuery({
    queryKey: ['expenditure-apportionment', incomeYear],
    queryFn: () =>
      customInstance<{ data: ApportionmentView }>({
        url: '/api/expenditure/apportionment',
        method: 'GET',
        params: { incomeYear },
      }).then((r) => r.data),
  });

  // Excluded / non-claimable costs for the FY — the EY-fee / setup-cost audit trail. Fetched
  // independently of the All-Line-Items filters and rendered in a collapsed section with reasons.
  const excludedQuery = useQuery({
    queryKey: ['expenditure-excluded', incomeYear],
    queryFn: () =>
      customInstance<{ data: LineItemsData }>({
        url: '/api/expenditure/line-items',
        method: 'GET',
        params: { incomeYear, limit: 500 },
      }).then((r) => r.data),
  });

  const { data: projectsData } = useGetProjects();
  const projects = (projectsData?.data?.items ?? []) as Array<{ id: string; name: string; status?: string }>;
  const activeProjects = projects.filter((p) => p.status !== 'archived');

  const summary = summaryQuery.data;
  const lineData = lineItemsQuery.data;

  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ['expenditure-summary', incomeYear] });
    void qc.invalidateQueries({ queryKey: ['expenditure-line-items', incomeYear] });
    void qc.invalidateQueries({ queryKey: ['expenditure-excluded', incomeYear] });
  };

  const excludedItems = useMemo(
    () => (excludedQuery.data?.items ?? []).filter((i) => i.eligibilityStatus === 'excluded'),
    [excludedQuery.data],
  );

  const handleSync = async (providerKey: string) => {
    const fyRange = parseIncomeYear(incomeYear);
    if (!fyRange) return;

    setSyncingProviders((prev) => new Set([...prev, providerKey]));
    try {
      const r = await customInstance<{ data: { created: number; updated: number; unchanged: number; rows?: number; warnings?: string[] } }>({
        url: `/api/expenditure/sync/${providerKey}`,
        method: 'POST',
        data: { periodStart: fyRange.start, periodEnd: fyRange.end },
      });
      setSyncStatus((prev) => ({
        ...prev,
        [providerKey]: { ...r.data, at: new Date().toISOString() },
      }));
      invalidateAll();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'Sync failed';
      setSyncStatus((prev) => ({
        ...prev,
        [providerKey]: { created: 0, updated: 0, unchanged: 0, rows: 0, at: new Date().toISOString(), error: msg },
      }));
    } finally {
      setSyncingProviders((prev) => {
        const next = new Set(prev);
        next.delete(providerKey);
        return next;
      });
    }
  };

  const handleSyncAll = async () => {
    const configured = PROVIDER_META.filter(
      (pm) => pm.integrationKey && summary?.configuredProviders.includes(pm.integrationKey)
    );
    for (const pm of configured) {
      await handleSync(pm.integrationKey);
    }
  };

  const handleClearAndResync = async (providerKey: string) => {
    const fyRange = parseIncomeYear(incomeYear);
    if (!fyRange) return;

    setSyncingProviders((prev) => new Set([...prev, providerKey]));
    try {
      const r = await customInstance<{ data: { created: number; updated: number; unchanged: number; rows?: number; warnings?: string[] } }>({
        url: `/api/expenditure/sync/${providerKey}`,
        method: 'POST',
        data: { periodStart: fyRange.start, periodEnd: fyRange.end, clearFirst: true },
      });
      setSyncStatus((prev) => ({
        ...prev,
        [providerKey]: { ...r.data, at: new Date().toISOString() },
      }));
      invalidateAll();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'Clear & re-sync failed';
      setSyncStatus((prev) => ({
        ...prev,
        [providerKey]: { created: 0, updated: 0, unchanged: 0, rows: 0, at: new Date().toISOString(), error: msg },
      }));
    } finally {
      setSyncingProviders((prev) => {
        const next = new Set(prev);
        next.delete(providerKey);
        return next;
      });
    }
  };

  const handleAssign = async (itemId: string, projectId: string | null) => {
    setAssigningItems((prev) => new Set([...prev, itemId]));
    try {
      await customInstance({
        url: `/api/cost-allocations/${itemId}/assign`,
        method: 'PATCH',
        data: { projectId },
      });
      invalidateAll();
    } finally {
      setAssigningItems((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const handleSetVendor = async (itemId: string, vendor: string) => {
    await customInstance({
      url: `/api/cost-allocations/${itemId}`,
      method: 'PATCH',
      data: { vendor: vendor.trim().toLowerCase() },
    });
    invalidateAll();
  };

  const handleUpsertOverride = async (
    provider: string,
    externalId: string,
    projectId: string,
    label: string,
    splitPercentage?: number | null,
  ) => {
    await customInstance({
      url: '/api/expenditure/overrides',
      method: 'PUT',
      data: { provider, externalId, projectId, label, splitPercentage: splitPercentage ?? null },
    });
    void qc.invalidateQueries({ queryKey: ['expenditure-accounts'] });
    void qc.invalidateQueries({ queryKey: ['expenditure-overrides'] });
  };

  const handleDeleteOverride = async (id: string) => {
    await customInstance({
      url: `/api/expenditure/overrides/${id}`,
      method: 'DELETE',
    });
    void qc.invalidateQueries({ queryKey: ['expenditure-accounts'] });
    void qc.invalidateQueries({ queryKey: ['expenditure-overrides'] });
  };

  const toggleProjectExpand = (pid: string | null) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const scrollToUnattributed = () => {
    setExpandedProjects((prev) => new Set([...prev, null]));
    setTimeout(() => {
      unattributedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const sourceLabel = (src: string): string => {
    return PROVIDER_META.find((p) => p.source === src)?.label ?? src;
  };

  // ── Grouping helpers ────────────────────────────────────────────────────
  function getGroupKey(item: CostAllocation): string {
    if (!item.sourceRef) return `${item.source}:${item.category}:${item.description ?? ''}`;
    // Strip trailing date range (YYYY-MM-DD:YYYY-MM-DD) from sourceRef
    return item.sourceRef.replace(/:\d{4}-\d{2}-\d{2}:\d{4}-\d{2}-\d{2}$/, '');
  }

  function getGroupLabel(item: CostAllocation): string {
    // Anthropic description varies per month (different token counts) — use generic label
    if (item.source === 'api_anthropic') return 'Anthropic API';
    // Manual entries without a description: show the category label so entries are distinguishable
    if (item.source === 'manual') {
      if (!item.description) {
        const catLabels: Record<string, string> = {
          cloud: 'Cloud Hosting', llm_tokens: 'AI / LLM', software_license: 'Software Licence',
          hardware: 'Hardware', other: 'Other',
        };
        return catLabels[item.category] ?? 'Manual';
      }
      return item.description;
    }
    if (!item.description) return sourceLabel(item.source);
    // AWS / others format: "ServiceName · Month YY · AccountAlias"
    // Strip the middle month segment, keep service + account alias
    const parts = item.description.split(' · ');
    if (parts.length >= 3) return [parts[0], ...parts.slice(2)].join(' · ');
    if (parts.length === 2) return parts[0]!;
    return item.description;
  }

  const lineItemGroups = useMemo(() => {
    const items = lineData?.items ?? [];
    const map = new Map<string, { key: string; label: string; source: string; items: CostAllocation[]; total: number }>();
    for (const item of items) {
      const key = getGroupKey(item);
      if (!map.has(key)) {
        map.set(key, { key, label: getGroupLabel(item), source: item.source, items: [], total: 0 });
      }
      const g = map.get(key)!;
      g.items.push(item);
      g.total += parseFloat(item.amount);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [lineData]);

  const toggleGroup = (key: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  // Group overrides by (provider, externalId) for the Account Mappings table
  const groupedOverrides = useMemo(() => {
    const map = new Map<string, { provider: string; externalId: string; label: string | null; rows: ProjectCostOverride[] }>();
    for (const ov of overridesQuery.data ?? []) {
      const key = `${ov.provider}:${ov.externalId}`;
      if (!map.has(key)) map.set(key, { provider: ov.provider, externalId: ov.externalId, label: ov.label, rows: [] });
      map.get(key)!.rows.push(ov);
    }
    return [...map.values()];
  }, [overridesQuery.data]);

  // Loading state
  if (summaryQuery.isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 rounded skeleton" />
        <div className="h-32 w-full rounded-lg skeleton" />
        <div className="h-48 w-full rounded-lg skeleton" />
      </div>
    );
  }

  // Error state
  if (summaryQuery.error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>Failed to load expenditure data.</span>
        <button onClick={() => void summaryQuery.refetch()} className="ml-auto underline text-xs">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Non-Labour Expenditure</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Cloud, AI, licences, and hardware R&D costs for {formatFy(incomeYear)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={incomeYear}
            onChange={(e) => { setIncomeYear(e.target.value); }}
            className="h-8 rounded-md border border-border bg-background px-3 text-sm shadow-sm"
            aria-label="Income year"
          >
            {fyOptions().map((fy) => (
              <option key={fy} value={fy}>{formatFy(fy)}</option>
            ))}
          </select>
          <Button variant="primary" size="sm" onClick={() => setAddPanelOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add cost
          </Button>
        </div>
      </div>

      {/* ── FX conversion rate ───────────────────────────────────────────── */}
      <FxRateBanner
        fx={summary?.fx}
        onSaved={() => {
          void qc.invalidateQueries({ queryKey: ['expenditure-summary'] });
          void qc.invalidateQueries({ queryKey: ['expenditure-line-items'] });
        }}
      />

      {/* ── GL reconciliation setup checklist ────────────────────────────── */}
      <GlSetupChecklist status={glSetupStatusQuery.data} />

      {/* ── Overhead apportionment methodology ───────────────────────────── */}
      <MethodologyCard
        incomeYear={incomeYear}
        view={apportionmentQuery.data}
        isLoading={apportionmentQuery.isLoading}
        error={!!apportionmentQuery.error}
        onRetry={() => void apportionmentQuery.refetch()}
        onSaved={(next) => {
          qc.setQueryData(['expenditure-apportionment', incomeYear], next);
        }}
      />

      {/* ── Provider card ────────────────────────────────────────────────── */}
      <div className="rounded-lg border bg-card p-5">
        <div className="flex items-stretch gap-6">

          {/* Left col: captured total + claimable (post-eligibility) + unattributed badge */}
          <div className="flex flex-col justify-center pr-6 border-r shrink-0">
            <span className="text-3xl font-bold tracking-tight tabular-nums leading-none">
              {formatAUD(summary?.totalAud ?? 0)}
            </span>
            <span className="text-xs text-muted-foreground mt-1">captured · {incomeYear}</span>
            <span className="text-sm font-semibold tabular-nums leading-none mt-3 text-emerald-700 dark:text-emerald-400">
              {formatAUD(summary?.totalClaimable ?? 0)}
            </span>
            <span className="text-xs text-muted-foreground mt-1">Claimable (post-eligibility)</span>
            {(summary?.unattributedCount ?? 0) > 0 && (
              <button
                onClick={scrollToUnattributed}
                className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 transition-colors"
              >
                <TriangleAlert className="h-3 w-3 text-amber-600" aria-hidden="true" />
                {summary!.unattributedCount} unattributed →
              </button>
            )}
          </div>

          {/* Right col: DATA SOURCES label + Sync all + pills + status log */}
          <div className="flex-1 min-w-0 flex flex-col gap-2.5">

            {/* Top row: label + Sync all */}
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Data Sources
              </span>
              <button
                onClick={() => void handleSyncAll()}
                disabled={syncingProviders.size > 0}
                className="inline-flex items-center gap-1.5 h-7 rounded-md bg-foreground text-background px-3 text-xs font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                <RotateCcw className={`h-3 w-3 ${syncingProviders.size > 0 ? 'animate-spin' : ''}`} aria-hidden="true" />
                Sync all
              </button>
            </div>

            {/* Provider pills */}
            <div className="flex flex-wrap gap-1.5">
              {PROVIDER_META.filter((pm) => pm.source !== 'manual' && pm.source !== 'api_xero').map((pm) => {
                const isConfigured = summary?.configuredProviders.includes(pm.integrationKey);
                const provData = summary?.byProvider[pm.source];
                const isSyncing = syncingProviders.has(pm.integrationKey);

                if (!isConfigured) {
                  return (
                    <div
                      key={pm.source}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-dashed px-2 py-1.5 opacity-55"
                      style={{ borderColor: pm.pillBorder, background: pm.pillBg }}
                    >
                      <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: pm.dotColor }} aria-hidden="true" />
                      <span className="text-xs font-semibold" style={{ color: pm.textColor }}>{pm.label}</span>
                      <button
                        onClick={() => navigate('/integrations')}
                        className="text-xs font-medium text-primary hover:underline ml-0.5"
                      >
                        Connect
                      </button>
                    </div>
                  );
                }

                return (
                  <div
                    key={pm.source}
                    className="inline-flex items-center gap-2 rounded-lg border px-2 py-1.5"
                    style={{ background: pm.pillBg, borderColor: pm.pillBorder }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: pm.dotColor }} aria-hidden="true" />
                    <div>
                      <div className="text-[11px] font-semibold leading-tight" style={{ color: pm.textColor }}>{pm.label}</div>
                      <div className="text-[11px] tabular-nums leading-tight" style={{ color: pm.subTextColor }}>
                        {formatAUD(provData?.totalAud ?? 0)}
                      </div>
                    </div>
                    <button
                      onClick={() => void handleSync(pm.integrationKey)}
                      disabled={isSyncing || syncingProviders.size > 0}
                      title={`Sync ${pm.label} (current FY)`}
                      aria-label={`Sync ${pm.label}`}
                      className="ml-0.5 h-5 w-5 rounded border flex items-center justify-center disabled:opacity-40 hover:opacity-80 transition-opacity"
                      style={{ borderColor: pm.pillBorder, background: 'rgba(255,255,255,0.7)', color: pm.textColor }}
                    >
                      <RotateCcw className={`h-2.5 w-2.5 ${isSyncing ? 'animate-spin' : ''}`} aria-hidden="true" />
                    </button>
                    {/* Clear & re-sync — account-level providers only (aws, gcp) */}
                    {(pm.integrationKey === 'aws' || pm.integrationKey === 'gcp') && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Clear ALL existing ${pm.label} cost data and re-sync the full FY from ${pm.label}? This cannot be undone.`)) {
                            void handleClearAndResync(pm.integrationKey);
                          }
                        }}
                        disabled={isSyncing || syncingProviders.size > 0}
                        title={`Clear all ${pm.label} data and re-sync full FY`}
                        aria-label={`Clear and re-sync ${pm.label}`}
                        className="ml-0.5 h-5 w-5 rounded border flex items-center justify-center disabled:opacity-40 hover:opacity-80 transition-opacity text-[9px] font-bold"
                        style={{ borderColor: pm.pillBorder, background: 'rgba(255,255,255,0.7)', color: pm.textColor }}
                      >
                        ✕↺
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Manual pill — no sync button */}
              {(() => {
                const pm = PROVIDER_META.find((p) => p.source === 'manual')!;
                const bmc = summary?.byManualCategory;
                const manualTotal = summary?.byProvider['manual']?.totalAud ?? 0;
                // Category label map for compact display
                const CAT_SHORT: Record<string, string> = {
                  cloud: 'Cloud', llm_tokens: 'AI', software_license: 'Software',
                  hardware: 'Hardware', other: 'Other', admin_overhead: 'Admin',
                  software_subscription: 'Subscriptions', insurance: 'Insurance',
                  consultant_fees: 'Consultants', company_setup: 'Setup', rdti_claim_prep: 'Claim Prep',
                };
                const subBreakdown = bmc
                  ? Object.entries(bmc).filter(([, v]) => v > 0).map(([k, v]) => `${CAT_SHORT[k] ?? k} ${formatAUD(v)}`).join(' · ')
                  : null;
                return (
                  <div
                    key="manual"
                    className="inline-flex items-center gap-2 rounded-lg border px-2 py-1.5"
                    style={{ background: pm.pillBg, borderColor: pm.pillBorder }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: pm.dotColor }} aria-hidden="true" />
                    <div>
                      <div className="text-[11px] font-semibold leading-tight" style={{ color: pm.textColor }}>{pm.label}</div>
                      <div className="text-[11px] tabular-nums leading-tight" style={{ color: pm.subTextColor }}>
                        {formatAUD(manualTotal)}
                      </div>
                      {subBreakdown && (
                        <div className="text-[10px] leading-tight opacity-70 mt-0.5" style={{ color: pm.subTextColor }}>
                          {subBreakdown}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Single-line sync status log */}
            {(syncingProviders.size > 0 || Object.keys(syncStatus).length > 0) && (
              <div className="flex items-center flex-wrap gap-0 pt-2 border-t">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mr-3 whitespace-nowrap">
                  Last Sync
                </span>
                {PROVIDER_META.filter((pm) => pm.integrationKey).map((pm) => {
                  const hasSyncEntry = syncingProviders.has(pm.integrationKey) || syncStatus[pm.integrationKey];
                  if (!hasSyncEntry) return null;
                  const visibleProviders = PROVIDER_META.filter((p) =>
                    syncingProviders.has(p.integrationKey) || syncStatus[p.integrationKey]
                  );
                  const isLast = visibleProviders.at(-1)?.integrationKey === pm.integrationKey;
                  return (
                    <span key={pm.source} className="inline-flex items-center">
                      <SyncStatusEntry
                        label={pm.label}
                        status={syncStatus[pm.integrationKey]}
                        isSyncing={syncingProviders.has(pm.integrationKey)}
                      />
                      {!isLast && <span className="text-muted-foreground/30 mx-2.5 text-sm">·</span>}
                    </span>
                  );
                })}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ── Xero cost review queue ───────────────────────────────────────── */}
      <CandidateQueue
        incomeYear={incomeYear}
        effectiveDefaultPercent={apportionmentQuery.data?.effectiveDefaultPercent ?? null}
        onChanged={invalidateAll}
      />

      {/* ── Account Mappings ──────────────────────────────────────────── */}
      {((accountsQuery.data?.length ?? 0) > 0 || (overridesQuery.data?.length ?? 0) > 0) && (
        <div className="rounded-lg border bg-card overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-5 py-3 hover:bg-muted/30 transition-colors"
            onClick={() => setMappingOpen((o) => !o)}
          >
            <span className="font-medium text-sm">Account Mappings</span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              {overridesQuery.data?.length ?? 0} mapped
              {mappingOpen
                ? <ChevronDown className="h-3.5 w-3.5" />
                : <ChevronRight className="h-3.5 w-3.5" />}
            </span>
          </button>
          {mappingOpen && (
            <div className="border-t">
              {/* Mapped accounts — grouped by account, with per-project split support */}
              {groupedOverrides.length > 0 ? (
                <table className="w-full text-sm">
                  <tbody>
                    {groupedOverrides.map((group) => {
                      const pm = PROVIDER_META.find((p) => p.integrationKey === group.provider);
                      const acctData = (accountsQuery.data ?? []).find(
                        (a) => a.provider === group.provider && a.externalId === group.externalId,
                      );
                      const isSplit = group.rows.length > 1 || group.rows.some((r) => r.splitPercentage !== null);
                      const pendingKey = `${group.provider}:${group.externalId}`;
                      const pending = pendingSplits[pendingKey];
                      const totalPct = group.rows.reduce((s, r) => s + (r.splitPercentage ? parseFloat(r.splitPercentage) : 0), 0);

                      return (
                        <>
                          {/* Account header row */}
                          <tr key={`header:${pendingKey}`} className="border-b bg-muted/10">
                            <td className="px-4 py-2" colSpan={3}>
                              <div className="flex items-center gap-2">
                                {pm && (
                                  <span
                                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold shrink-0"
                                    style={{ background: pm.pillBg, color: pm.textColor, border: `1px solid ${pm.pillBorder}` }}
                                  >
                                    {pm.label}
                                  </span>
                                )}
                                <span className="font-medium text-sm">{group.label ?? group.externalId}</span>
                                {group.label && (
                                  <span className="text-xs text-muted-foreground">({group.externalId})</span>
                                )}
                                {acctData && acctData.totalAud > 0 && (
                                  <span className="text-xs text-muted-foreground">
                                    ${acctData.totalAud.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                )}
                                {isSplit && totalPct > 0 && totalPct !== 100 && (
                                  <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 ml-auto">
                                    {totalPct}% allocated
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                          {/* Split assignment rows */}
                          {group.rows.map((ov) => (
                            <tr key={ov.id} className="border-b last:border-0 hover:bg-muted/20">
                              <td className="px-4 py-2 pl-8">
                                <select
                                  className="h-7 rounded-md border border-border bg-background px-2 text-xs shadow-sm"
                                  value={ov.projectId}
                                  onChange={(e) => void handleUpsertOverride(
                                    ov.provider, ov.externalId, e.target.value,
                                    ov.label ?? ov.externalId,
                                    ov.splitPercentage !== null ? parseFloat(ov.splitPercentage) : null,
                                  )}
                                >
                                  {activeProjects.map((p) => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-2 w-28">
                                {isSplit ? (
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="number"
                                      min={1}
                                      max={100}
                                      step={1}
                                      className="h-7 w-16 rounded-md border border-border bg-background px-2 text-xs shadow-sm"
                                      defaultValue={ov.splitPercentage ? parseFloat(ov.splitPercentage) : ''}
                                      placeholder="%"
                                      onBlur={(e) => {
                                        const pct = parseFloat(e.target.value);
                                        if (!isNaN(pct) && pct > 0 && pct <= 100) {
                                          void handleUpsertOverride(ov.provider, ov.externalId, ov.projectId, ov.label ?? ov.externalId, pct);
                                        }
                                      }}
                                    />
                                    <span className="text-xs text-muted-foreground">%</span>
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">100%</span>
                                )}
                              </td>
                              <td className="px-4 py-2 w-10">
                                <button
                                  onClick={() => void handleDeleteOverride(ov.id)}
                                  className="text-muted-foreground hover:text-destructive transition-colors"
                                  aria-label={`Remove mapping for ${ov.label ?? ov.externalId}`}
                                >
                                  ✕
                                </button>
                              </td>
                            </tr>
                          ))}
                          {/* Pending new split row */}
                          {pending && (
                            <tr key={`pending:${pendingKey}`} className="border-b bg-blue-50/30">
                              <td className="px-4 py-2 pl-8">
                                <select
                                  className="h-7 rounded-md border border-border bg-background px-2 text-xs shadow-sm"
                                  value={pending.projectId}
                                  onChange={(e) => setPendingSplits((p) => ({ ...p, [pendingKey]: { ...pending, projectId: e.target.value } }))}
                                >
                                  <option value="" disabled>Project…</option>
                                  {activeProjects.map((p) => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-2 w-28">
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    min={1}
                                    max={100}
                                    step={1}
                                    className="h-7 w-16 rounded-md border border-border bg-background px-2 text-xs shadow-sm"
                                    value={pending.pct}
                                    placeholder="%"
                                    onChange={(e) => setPendingSplits((p) => ({ ...p, [pendingKey]: { ...pending, pct: e.target.value } }))}
                                  />
                                  <span className="text-xs text-muted-foreground">%</span>
                                </div>
                              </td>
                              <td className="px-4 py-2 w-24">
                                <div className="flex gap-1">
                                  <button
                                    className="text-xs text-primary hover:underline"
                                    onClick={() => {
                                      const pct = parseFloat(pending.pct);
                                      if (!pending.projectId || isNaN(pct)) return;
                                      void handleUpsertOverride(group.provider, group.externalId, pending.projectId, group.label ?? group.externalId, pct);
                                      // Also update existing solo rows to have their percentage
                                      if (group.rows.length === 1 && group.rows[0]!.splitPercentage === null) {
                                        void handleUpsertOverride(group.provider, group.externalId, group.rows[0]!.projectId, group.label ?? group.externalId, 100 - pct > 0 ? 100 - pct : null);
                                      }
                                      setPendingSplits((p) => { const n = { ...p }; delete n[pendingKey]; return n; });
                                    }}
                                  >Save</button>
                                  <button
                                    className="text-xs text-muted-foreground hover:text-destructive"
                                    onClick={() => setPendingSplits((p) => { const n = { ...p }; delete n[pendingKey]; return n; })}
                                  >Cancel</button>
                                </div>
                              </td>
                            </tr>
                          )}
                          {/* Add split button */}
                          {!pending && (
                            <tr key={`add:${pendingKey}`} className="border-b">
                              <td className="px-4 py-1.5 pl-8" colSpan={3}>
                                <button
                                  className="text-xs text-muted-foreground hover:text-primary transition-colors"
                                  onClick={() => {
                                    const defaultProject = activeProjects.find((p) => !group.rows.some((r) => r.projectId === p.id));
                                    setPendingSplits((p) => ({ ...p, [pendingKey]: { projectId: defaultProject?.id ?? '', pct: '' } }));
                                  }}
                                >
                                  + Add split
                                </button>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="px-5 py-3 text-xs text-muted-foreground">No mappings yet — assign accounts below.</p>
              )}

              {/* Unmapped discovered accounts */}
              {(() => {
                const unmapped = (accountsQuery.data ?? []).filter((a) => !a.overrideId && (!a.splits || a.splits.length === 0));
                if (unmapped.length === 0) return null;
                return (
                  <div className="border-t bg-muted/20 px-4 py-3">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                      Discovered · unassigned
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {unmapped.map((acct) => {
                        const pm = PROVIDER_META.find((p) => p.integrationKey === acct.provider);
                        return (
                          <div
                            key={`${acct.provider}:${acct.externalId}`}
                            className="flex flex-col gap-1.5 rounded-lg border px-3 py-2 bg-background text-xs min-w-[260px] max-w-sm"
                          >
                            {/* Header row */}
                            <div className="flex items-center gap-2 flex-wrap">
                              {pm && (
                                <span
                                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold shrink-0"
                                  style={{ background: pm.pillBg, color: pm.textColor, border: `1px solid ${pm.pillBorder}` }}
                                >
                                  {pm.label}
                                </span>
                              )}
                              <span className="font-semibold">{acct.label}</span>
                              <span className="text-muted-foreground text-[10px]">{acct.externalId}</span>
                              {acct.totalAud > 0 && (
                                <span className="font-semibold ml-auto">
                                  ${acct.totalAud.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              )}
                            </div>
                            {/* Service breakdown */}
                            {acct.services.length > 0 && (
                              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground border-t pt-1">
                                {acct.services.map((svc) => (
                                  <span key={svc.name}>
                                    {svc.name.replace('Amazon ', '').replace('AWS ', '')}: <span className="font-medium tabular-nums">${svc.totalAud.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                            {/* Assign dropdown */}
                            <select
                              className="h-6 rounded border border-border bg-background px-1.5 text-xs mt-0.5"
                              defaultValue=""
                              onChange={(e) => {
                                if (e.target.value) void handleUpsertOverride(acct.provider, acct.externalId, e.target.value, acct.label);
                              }}
                            >
                              <option value="" disabled>Assign to project…</option>
                              {activeProjects.map((p) => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                              ))}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* ── Monthly trend chart ──────────────────────────────────────────── */}
      <div className="rounded-lg border p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-sm">Monthly Trend</h2>
          <div className="flex rounded-md border overflow-hidden text-xs">
            <button
              onClick={() => setChartMode('provider')}
              className={`px-3 py-1.5 transition-colors ${chartMode === 'provider' ? 'bg-primary text-primary-foreground font-medium' : 'bg-background hover:bg-muted'}`}
            >
              By Provider
            </button>
            <button
              onClick={() => setChartMode('category')}
              className={`px-3 py-1.5 transition-colors ${chartMode === 'category' ? 'bg-primary text-primary-foreground font-medium' : 'bg-background hover:bg-muted'}`}
            >
              By Category
            </button>
          </div>
        </div>

        {summary && summary.monthlyByProvider.length > 0 ? (
          <TrendChart
            data={chartMode === 'provider' ? summary.monthlyByProvider : summary.monthlyByCategory}
            mode={chartMode}
          />
        ) : (
          <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">
            No expenditure data for {formatFy(incomeYear)}
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {chartMode === 'provider'
            ? PROVIDER_META.map((item) => (
              <div key={item.source} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="h-2 w-2 rounded-full shrink-0" style={{ background: item.color }} aria-hidden="true" />
                {item.label}
              </div>
            ))
            : CATEGORY_META.map((item) => (
              <div key={item.key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="h-2 w-2 rounded-full shrink-0" style={{ background: item.color }} aria-hidden="true" />
                {item.label}
              </div>
            ))
          }
        </div>
      </div>

      {/* ── By project table ─────────────────────────────────────────────── */}
      <div className="rounded-lg border overflow-hidden">
        <div className="p-4 border-b bg-muted/30">
          <h2 className="font-medium text-sm">By Project</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="text-left font-medium px-4 py-2">Project</th>
                <th className="text-right font-medium px-3 py-2">Cloud</th>
                <th className="text-right font-medium px-3 py-2">AI / LLM</th>
                <th className="text-right font-medium px-3 py-2">Licences</th>
                <th className="text-right font-medium px-3 py-2">Hardware</th>
                <th className="text-right font-medium px-3 py-2">Other</th>
                <th className="text-right font-medium px-4 py-2">Total</th>
                <th className="text-right font-medium px-4 py-2">Share</th>
              </tr>
            </thead>
            <tbody>
              {(summary?.byProject ?? []).map((proj) => {
                const isExpanded = expandedProjects.has(proj.projectId);
                const pct = summary && summary.totalAud > 0
                  ? Math.round((proj.totalAud / summary.totalAud) * 100)
                  : 0;
                const isUnattributed = proj.projectId === null;

                return (
                  <>
                    <tr
                      key={proj.projectId ?? 'unattributed'}
                      ref={isUnattributed ? unattributedRef : null}
                      className={`border-b hover:bg-muted/30 cursor-pointer transition-colors ${isUnattributed ? 'bg-amber-50/50' : ''}`}
                      onClick={() => toggleProjectExpand(proj.projectId)}
                    >
                      <td className="px-4 py-3 font-medium">
                        <div className="flex items-center gap-2">
                          {isExpanded
                            ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                            : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                          {isUnattributed ? (
                            <span className="text-amber-700">Unattributed</span>
                          ) : proj.projectName}
                        </div>
                      </td>
                      <td className="text-right px-3 py-3 font-mono text-xs">{proj.cloud > 0 ? formatAUD(proj.cloud) : '—'}</td>
                      <td className="text-right px-3 py-3 font-mono text-xs">{proj.llm_tokens > 0 ? formatAUD(proj.llm_tokens) : '—'}</td>
                      <td className="text-right px-3 py-3 font-mono text-xs">{proj.software_license > 0 ? formatAUD(proj.software_license) : '—'}</td>
                      <td className="text-right px-3 py-3 font-mono text-xs">{proj.hardware > 0 ? formatAUD(proj.hardware) : '—'}</td>
                      <td className="text-right px-3 py-3 font-mono text-xs">{proj.other > 0 ? formatAUD(proj.other) : '—'}</td>
                      <td className="text-right px-4 py-3 font-mono font-medium text-xs">{formatAUD(proj.totalAud)}</td>
                      <td className="text-right px-4 py-3">
                        <div className="inline-flex items-center gap-1.5 justify-end">
                          <div className="w-10 h-1 rounded-full bg-muted overflow-hidden">
                            <div
                              className={`h-full rounded-full ${isUnattributed ? 'bg-amber-400' : 'bg-primary'}`}
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                          </div>
                          <span className={`text-xs tabular-nums ${isUnattributed ? 'text-amber-600' : 'text-muted-foreground'}`}>{pct}%</span>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <ProjectItemsExpanded
                        projectId={proj.projectId}
                        incomeYear={incomeYear}
                        isUnattributed={isUnattributed}
                      />
                    )}
                  </>
                );
              })}
              {(summary?.byProject ?? []).length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    No expenditure recorded for {formatFy(incomeYear)}. Use Sync now or Add cost to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── All line items ───────────────────────────────────────────────── */}
      <div className="rounded-lg border overflow-hidden">
        <div className="p-4 border-b bg-muted/30 flex flex-wrap items-center gap-3">
          <h2 className="font-medium text-sm">All Line Items</h2>
          <div className="flex flex-wrap gap-2 ml-auto">
            <select
              value={providerFilter}
              onChange={(e) => { setProviderFilter(e.target.value); }}
              className="h-7 rounded-md border border-border bg-background px-2 text-xs shadow-sm"
              aria-label="Filter by provider"
            >
              <option value="">All providers</option>
              {PROVIDER_META.map((p) => (
                <option key={p.source} value={p.source}>{p.label}</option>
              ))}
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); }}
              className="h-7 rounded-md border border-border bg-background px-2 text-xs shadow-sm"
              aria-label="Filter by category"
            >
              <option value="">All categories</option>
              {CATEGORY_META.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <input
              type="search"
              placeholder="Search description…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); }}
              className="h-7 rounded-md border border-border bg-background px-2 text-xs shadow-sm w-44"
              aria-label="Search line items"
            />
          </div>
        </div>

        {lineItemsQuery.isLoading ? (
          <div className="p-4 space-y-2">
            {[...Array(5)].map((_, i) => <div key={i} className="h-8 rounded skeleton" />)}
          </div>
        ) : lineItemsQuery.error ? (
          <div className="p-4 text-sm text-destructive flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span>Failed to load line items.</span>
            <button onClick={() => void lineItemsQuery.refetch()} className="underline text-xs">Retry</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="text-left font-medium px-4 py-2 w-5" />
                  <th className="text-left font-medium px-3 py-2">Provider</th>
                  <th className="text-left font-medium px-3 py-2">Service / Account</th>
                  <th className="text-left font-medium px-3 py-2 min-w-[160px]">Project</th>
                  <th className="text-right font-medium px-3 py-2">Entries</th>
                  <th className="text-right font-medium px-4 py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {lineItemGroups.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">
                      No line items match the current filters.
                    </td>
                  </tr>
                )}
                {lineItemGroups.map((group) => {
                  const isExpanded = expandedGroups.has(group.key);
                  const pm = PROVIDER_META.find((p) => p.source === group.source);
                  // Summarise projects in the group
                  const projectIds = new Set(group.items.map((i) => i.projectId));
                  const allUnattributed = [...projectIds].every((id) => id === null);
                  const allSame = projectIds.size === 1 && !allUnattributed;
                  const singleProjectName = allSame
                    ? (activeProjects.find((p) => p.id === [...projectIds][0])?.name ?? '—')
                    : null;

                  return (
                    <>
                      {/* Group summary row */}
                      <tr
                        key={group.key}
                        className={`border-b hover:bg-muted/30 cursor-pointer transition-colors ${allUnattributed ? 'bg-amber-50/30' : ''}`}
                        onClick={() => toggleGroup(group.key)}
                      >
                        <td className="px-4 py-2.5 text-muted-foreground">
                          {isExpanded
                            ? <ChevronDown className="h-3.5 w-3.5" />
                            : <ChevronRight className="h-3.5 w-3.5" />}
                        </td>
                        <td className="px-3 py-2.5">
                          {pm ? (
                            <span
                              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap"
                              style={{ background: pm.pillBg, color: pm.textColor, border: `1px solid ${pm.pillBorder}` }}
                            >
                              {pm.label}
                            </span>
                          ) : (
                            <span className="text-xs">{sourceLabel(group.source)}</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-xs font-medium">{group.label}</td>
                        <td className="px-3 py-2.5 text-xs">
                          {allUnattributed ? (
                            <span className="text-amber-700 font-medium">Unattributed</span>
                          ) : allSame ? (
                            <span className="text-muted-foreground">{singleProjectName}</span>
                          ) : (
                            <span className="text-muted-foreground">{projectIds.size} projects</span>
                          )}
                        </td>
                        <td className="text-right px-3 py-2.5 text-xs text-muted-foreground tabular-nums">
                          {group.items.length}
                        </td>
                        <td className="text-right px-4 py-2.5 font-mono text-xs font-medium">
                          {formatAUD(group.total)}
                        </td>
                      </tr>
                      {/* Expanded per-item rows */}
                      {isExpanded && group.items.map((item) => {
                        const isAssigning = assigningItems.has(item.id);
                        return (
                          <tr key={item.id} className={`border-b text-xs ${!item.projectId ? 'bg-amber-50/20' : 'bg-muted/10'}`}>
                            <td className="px-4 py-2" />
                            <td className="px-3 py-2 font-mono text-muted-foreground">{item.periodStart}</td>
                            <td className="px-3 py-2 text-muted-foreground max-w-xs truncate">{item.description ?? '—'}</td>
                            <td className="px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                              <select
                                value={item.projectId ?? ''}
                                disabled={isAssigning}
                                onChange={(e) => void handleAssign(item.id, e.target.value || null)}
                                className={`w-full h-[24px] rounded border px-1.5 text-xs shadow-sm disabled:opacity-50 ${!item.projectId ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-border bg-background'}`}
                                aria-label="Assign to project"
                              >
                                <option value="">— Unattributed —</option>
                                {activeProjects.map((p) => (
                                  <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                              </select>
                              {item.source === 'manual' && (
                                <input
                                  type="text"
                                  list="vendor-options-inline"
                                  defaultValue={item.vendor ?? ''}
                                  placeholder="vendor"
                                  maxLength={50}
                                  onBlur={(e) => {
                                    const v = e.target.value.trim().toLowerCase();
                                    if (v !== (item.vendor ?? '')) void handleSetVendor(item.id, v);
                                  }}
                                  className="mt-1 w-full h-[22px] rounded border border-border bg-background px-1.5 text-[11px]"
                                  aria-label="Set vendor"
                                />
                              )}
                            </td>
                            <td className="px-3 py-2" />
                            <td className="text-right px-4 py-2 font-mono font-medium">{formatAUD(parseFloat(item.amount))}</td>
                          </tr>
                        );
                      })}
                    </>
                  );
                })}
              </tbody>
            </table>
            <datalist id="vendor-options-inline">
              <option value="aws" /><option value="gcp" /><option value="anthropic" />
              <option value="openai" /><option value="replit" /><option value="claude_teams" />
              <option value="github" /><option value="jetbrains" />
            </datalist>
            {lineData && (
              <div className="px-4 py-2.5 border-t text-xs text-muted-foreground">
                {lineItemGroups.length} groups · {lineData.pagination.total} line items
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Excluded costs (audit trail) ─────────────────────────────────────
          Four fetch states: a fetch failure must NEVER silently render as "no exclusions" —
          that failure direction misleads an auditor. Collapsed-when-empty applies only to
          genuine zero-exclusion data. */}
      {excludedQuery.isLoading ? (
        <div className="rounded-lg border bg-card p-5">
          <div className="h-5 w-64 rounded skeleton" />
        </div>
      ) : excludedQuery.error ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Failed to load excluded costs — the exclusion audit trail may be incomplete.</span>
          <button onClick={() => void excludedQuery.refetch()} className="ml-auto underline text-xs">Retry</button>
        </div>
      ) : excludedItems.length > 0 && (
        <div className="rounded-lg border bg-card overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-5 py-3 hover:bg-muted/30 transition-colors"
            onClick={() => setExcludedOpen((o) => !o)}
          >
            <span className="flex items-center gap-2 font-medium text-sm">
              Excluded costs
              <span className="text-xs font-normal text-muted-foreground">
                — not claimable, retained for audit
              </span>
            </span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              {excludedItems.length} excluded
              {excludedOpen
                ? <ChevronDown className="h-3.5 w-3.5" />
                : <ChevronRight className="h-3.5 w-3.5" />}
            </span>
          </button>
          {excludedOpen && (
            <div className="border-t overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="text-left font-medium px-4 py-2">Cost</th>
                    <th className="text-left font-medium px-3 py-2">Category</th>
                    <th className="text-left font-medium px-3 py-2 min-w-[240px]">Exclusion reason</th>
                    <th className="text-right font-medium px-4 py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {excludedItems.map((item) => (
                    <tr key={item.id} className="border-b last:border-0 text-xs">
                      <td className="px-4 py-2.5">
                        <div className="font-medium">{item.description ?? sourceLabel(item.source)}</div>
                        <div className="text-muted-foreground mt-0.5">{item.periodStart} → {item.periodEnd}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center rounded-md border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                          {COST_CATEGORY_OPTIONS.find((c) => c.value === item.category)?.label ?? item.category}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5">
                          <EligibilityChip status="excluded" />
                          <span>{item.eligibilityReason ?? '—'}</span>
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono">{formatAUD(parseFloat(item.amount))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Add Cost panel ───────────────────────────────────────────────── */}
      <AddCostPanel
        open={addPanelOpen}
        onClose={() => setAddPanelOpen(false)}
        projects={activeProjects}
        onSuccess={invalidateAll}
      />
    </div>
  );
}
